import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Paper, Grid, Button, CircularProgress,
    Card, CardContent, Divider, Chip, List, ListItem, ListItemText,
    ListItemIcon, IconButton, Alert, LinearProgress, Tooltip, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Tabs, Tab
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Assessment as AssessmentIcon,
    History as HistoryIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Assignment as AssignmentIcon,
    TrendingUp as TrendingUpIcon,
    LocalHospital as HospitalIcon,
    Close as CloseIcon,
    FilterList as FilterListIcon,
    Visibility as VisibilityIcon,
    PictureAsPdf as PictureAsPdfIcon,
    TableChart as TableChartIcon,
    List as ListIcon
} from '@mui/icons-material';
import { BarChart, LineChart, PieChart } from '@mui/x-charts';
import axios from 'axios';
import flaskServer from '../config/flaskserver';
import PivotTabelaProcessada from '../layout/PivotTabelaProcessada';

const AuditoriaTissDashboard = () => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [batchStatus, setBatchStatus] = useState('');
    const [batchProgress, setBatchProgress] = useState(0);
    const [analiseAtual, setAnaliseAtual] = useState(null);
    const [historico, setHistorico] = useState([]);
    const [convenios, setConvenios] = useState([]);
    const [selectedConvenio, setSelectedConvenio] = useState('null');
    const [error, setError] = useState('');

    // Filtros de Alertas
    const [filterType, setFilterType] = useState('Todos');
    const [minProb, setMinProb] = useState(0);
    const [filterProcedure, setFilterProcedure] = useState('Todos');
    const [filterGuia, setFilterGuia] = useState('Todos');

    // Modal de Detalhes
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedAlerta, setSelectedAlerta] = useState(null);
    const [detalhesAlerta, setDetalhesAlerta] = useState([]);
    const [loadingDetalhes, setLoadingDetalhes] = useState(false);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'pivot'

    const [tabValue, setTabValue] = useState(0);

    // Dashboard Data
    const [summaryData, setSummaryData] = useState({ 'Seguro': 0, 'Atenção': 0, 'Alto Risco': 0 });
    const [historicoGrafico, setHistoricoGrafico] = useState([]);
    const [topAlertas, setTopAlertas] = useState([]);

    useEffect(() => {
        carregarHistorico();
        carregarConvenios();
        carregarDashboard();
    }, [tabValue]);

    const carregarDashboard = async () => {
        if (tabValue !== 0) return;
        try {
            const token = localStorage.getItem('token');
            const [resSum, resHist, resTop] = await Promise.all([
                axios.get(`${flaskServer.flaskServerIP}/auditoria/analise/summary`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${flaskServer.flaskServerIP}/auditoria/analise/historico-grafico`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${flaskServer.flaskServerIP}/auditoria/analise/top-alertas`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setSummaryData(resSum.data.summary);
            setHistoricoGrafico(resHist.data.history);
            setTopAlertas(resTop.data.top_alertas);
        } catch (err) {
            console.error("Erro ao carregar dashboard:", err);
        }
    };

    const carregarConvenios = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${flaskServer.flaskServerIP}/convenios`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setConvenios(response.data.convenios || []);
        } catch (err) {
            console.error("Erro ao carregar convênios:", err);
        }
    };

    const carregarHistorico = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${flaskServer.flaskServerIP}/auditoria/historico`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setHistorico(response.data);
        } catch (err) {
            console.error("Erro ao carregar histórico:", err);
        }
    };

    const handleFileChange = (e) => {
        setFiles(Array.from(e.target.files));
        setError('');
    };

    const handleAnalisar = async () => {
        if (files.length === 0) return;
        setLoading(true);
        setError('');
        setBatchProgress(0);

        try {
            const token = localStorage.getItem('token');
            let lastAnalise = null;

            for (let i = 0; i < files.length; i++) {
                const currentFile = files[i];
                setBatchStatus(`Analisando arquivo ${i + 1} de ${files.length}: ${currentFile.name}`);
                setBatchProgress(((i) / files.length) * 100);

                const formData = new FormData();
                formData.append('file', currentFile);
                formData.append('convenio_id', selectedConvenio);

                const response = await axios.post(`${flaskServer.flaskServerIP}/auditoria/analisar`, formData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                lastAnalise = response.data;
            }

            setAnaliseAtual(lastAnalise);
            setBatchProgress(100);
            setBatchStatus('Processamento concluído!');
            carregarHistorico();
        } catch (err) {
            setError('Erro ao analisar guias: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleAprender = async () => {
        if (files.length === 0) return;
        if (!selectedConvenio || selectedConvenio === 'null') {
            setError('Por favor, selecione um convênio específico para alimentar o histórico.');
            return;
        }

        setLoading(true);
        setBatchProgress(0);

        try {
            const token = localStorage.getItem('token');
            for (let i = 0; i < files.length; i++) {
                const currentFile = files[i];
                setBatchStatus(`Aprendendo com arquivo ${i + 1} de ${files.length}: ${currentFile.name}`);
                setBatchProgress(((i) / files.length) * 100);

                const formData = new FormData();
                formData.append('file', currentFile);
                formData.append('convenio_id', selectedConvenio);

                await axios.post(`${flaskServer.flaskServerIP}/auditoria/aprender`, formData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
            }
            setBatchProgress(100);
            setBatchStatus('Histórico alimentado com sucesso!');
            alert('Padrões históricos processados com sucesso!');
        } catch (err) {
            setError('Erro no aprendizado: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleExportarRelatorio = async (analiseId) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${flaskServer.flaskServerIP}/auditoria/relatorio/${analiseId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `relatorio_auditoria_${analiseId}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError('Erro ao exportar relatório: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const getScoreColor = (score) => {
        if (score < 30) return '#4caf50';
        if (score < 60) return '#ff9800';
        return '#f44336';
    };

    const handleFeedback = async (alertaIdx, acao) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${flaskServer.flaskServerIP}/auditoria/feedback`, {
                guia: analiseAtual.guia,
                alerta_idx: alertaIdx,
                acao: acao
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            // Atualiza localmente o status do alerta
            const novosAlertas = [...analiseAtual.alertas];
            novosAlertas[alertaIdx].feedback = acao;
            setAnaliseAtual({ ...analiseAtual, alertas: novosAlertas });

        } catch (err) {
            alert('Erro ao processar feedback');
        }
    };

    const handleShowDetails = async (alerta, isHistoryItem = false) => {
        if (isHistoryItem) {
            // For history items, we want to show all items of that guide
            setSelectedAlerta({
                tipo: `Itens da Guia ${alerta.guia}`,
                codigo: 'Todos os itens desta guia',
                guia: alerta.guia
            });
        } else {
            setSelectedAlerta(alerta);
        }
        setModalOpen(true);
        setLoadingDetalhes(true);
        setDetalhesAlerta([]);

        try {
            const token = localStorage.getItem('token');
            const params = isHistoryItem ? {
                guia: alerta.guia
            } : {
                codigo: alerta.codigo || alerta.procedimento_codigo,
                tipo_alerta: alerta.tipo,
                item_faltante: alerta.item_faltante
            };

            const response = await axios.get(`${flaskServer.flaskServerIP}/auditoria/detalhes-alerta`, {
                headers: { 'Authorization': `Bearer ${token}` },
                params: params
            });
            setDetalhesAlerta(response.data);
        } catch (err) {
            console.error("Erro ao carregar detalhes do alerta:", err);
        } finally {
            setLoadingDetalhes(false);
        }
    };

    const handleIgnoreAllForProcedure = async (procCodigo) => {
        if (!window.confirm(`Deseja ignorar todos os alertas de omissão para o procedimento ${procCodigo}?`)) return;

        const alertasParaIgnorar = analiseAtual.alertas.filter(a =>
            a.tipo === 'Possível Omissão' && a.procedimento_codigo === procCodigo && !a.feedback
        );

        if (alertasParaIgnorar.length === 0) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            for (const alerta of alertasParaIgnorar) {
                const idx = analiseAtual.alertas.indexOf(alerta);
                await axios.post(`${flaskServer.flaskServerIP}/auditoria/feedback`, {
                    guia: analiseAtual.guia,
                    alerta_idx: idx,
                    acao: 'rejeitar'
                }, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            }

            // Atualiza localmente
            const novosAlertas = analiseAtual.alertas.map(a => {
                if (a.tipo === 'Possível Omissão' && a.procedimento_codigo === procCodigo) {
                    return { ...a, feedback: 'rejeitar' };
                }
                return a;
            });
            setAnaliseAtual({ ...analiseAtual, alertas: novosAlertas });
            alert('Regras de omissão ignoradas com sucesso!');
        } catch (err) {
            alert('Erro ao processar ignore em massa');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenPivotNewTab = (data, title = 'Análise Dinâmica') => {
        localStorage.setItem('audit_pivot_data', JSON.stringify({
            data: data,
            title: title
        }));
        window.open('/auditoria/pivot', '_blank');
    };

    const getFilteredAlerts = () => {
        if (!analiseAtual || !analiseAtual.alertas) return [];
        return analiseAtual.alertas.filter(alerta => {
            const matchesType = filterType === 'Todos' ||
                alerta.tipo === filterType ||
                (filterType === 'Código não encontrado na tabela do convênio' && alerta.tipo === 'Código não encontrado nas fontes do convênio') ||
                (filterType === 'Preço Acima da tabela do convênio' && alerta.tipo === 'Preço Acima da fonte do convênio') ||
                (filterType === 'Preço abaixo da tabela do convênio' && alerta.tipo === 'Preço abaixo da fonte do convênio');
            const matchesProb = alerta.tipo !== 'Possível Omissão' || (alerta.probabilidade || 0) >= minProb;
            const matchesProc = filterProcedure === 'Todos' || alerta.procedimento_codigo === filterProcedure;
            const matchesGuia = filterGuia === 'Todos' || alerta.guia === filterGuia;
            return matchesType && matchesProb && matchesProc && matchesGuia;
        });
    };

    const uniqueProcedures = analiseAtual?.alertas
        ? [...new Set(analiseAtual.alertas.filter(a => a.procedimento_codigo).map(a => JSON.stringify({ codigo: a.procedimento_codigo, desc: a.procedimento_desc })))]
            .map(s => JSON.parse(s))
        : [];

    const uniqueGuias = analiseAtual?.todas_guias || (analiseAtual?.alertas
        ? [...new Set(analiseAtual.alertas.filter(a => a.guia).map(a => a.guia))]
        : []);

    const filteredAlerts = getFilteredAlerts();

    return (
        <Container maxWidth={viewMode === 'pivot' ? false : "lg"} sx={{ mt: 4, mb: 4, transition: 'all 0.3s ease' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center">
                    <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                    <Box>
                        <Typography variant="h4" fontWeight="bold" color="primary">
                            Auditoria Inteligente TISS
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Análise baseada em inteligência estatística e histórico hospitalar
                        </Typography>
                    </Box>
                </Box>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{
                    '& .MuiTab-root': { fontWeight: 700, textTransform: 'none' }
                }}>
                    <Tab label="Dashboard Interativo" icon={<AssessmentIcon />} iconPosition="start" />
                    <Tab label="Auditoria Operacional" icon={<CloudUploadIcon />} iconPosition="start" />
                </Tabs>
            </Box>

            {tabValue === 0 && (
                <Grid container spacing={3}>
                    {/* Summary Cards */}
                    <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 3, borderRadius: 4, textAlign: 'center', bgcolor: '#fef3c7', color: '#92400e' }}>
                            <Typography variant="overline" sx={{ fontWeight: 700 }}>⚠️ Atenção</Typography>
                            <Typography variant="h3" fontWeight={800}>{summaryData['Atenção'] || 0}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>Análises (Score 30-60)</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 3, borderRadius: 4, textAlign: 'center', bgcolor: '#fee2e2', color: '#b91c1c' }}>
                            <Typography variant="overline" sx={{ opacity: 0.9, fontWeight: 700 }}>🚨 Alto Risco / Glosa</Typography>
                            <Typography variant="h3" fontWeight={800}>{summaryData['Alto Risco'] || 0}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>Análises (Score &gt; 60)</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 3, borderRadius: 4, textAlign: 'center', bgcolor: '#e0f2fe', color: '#0369a1' }}>
                            <Typography variant="overline" sx={{ fontWeight: 700 }}>✅ Seguro</Typography>
                            <Typography variant="h3" fontWeight={800}>{summaryData['Seguro'] || 0}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>Análises (Score &lt; 30)</Typography>
                        </Paper>
                    </Grid>

                    {/* Chart Row 1 */}
                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: 3, borderRadius: 4 }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom>Evolução da Média de Risco (Temporal)</Typography>
                            <Box sx={{ height: 350, width: '100%' }}>
                                <BarChart
                                    xAxis={[{ scaleType: 'band', data: historicoGrafico.map(h => h.data.split('-').slice(1).join('/')) || ['Sem dados'] }]}
                                    series={[
                                        { data: historicoGrafico.map(h => h.media_risco || 0), color: '#ef4444', label: 'Score Médio' }
                                    ]}
                                    height={350}
                                />
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, borderRadius: 4 }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom>Distribuição de Risco Histórico</Typography>
                            <Box sx={{ height: 350, width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <PieChart
                                    series={[{
                                        data: [
                                            { id: 0, value: summaryData['Alto Risco'] || 0, label: 'Alto Risco', color: '#ef4444' },
                                            { id: 1, value: summaryData['Atenção'] || 0, label: 'Atenção', color: '#f59e0b' },
                                            { id: 2, value: summaryData['Seguro'] || 0, label: 'Seguro', color: '#3b82f6' }
                                        ],
                                        innerRadius: 60,
                                        paddingAngle: 5,
                                        cornerRadius: 5,
                                    }]}
                                    height={350}
                                />
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Chart Row 2 */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, borderRadius: 4 }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom>Top 10 Regras Mais Quebradas</Typography>
                            <Box sx={{ height: 350, width: '100%' }}>
                                <BarChart
                                    layout="horizontal"
                                    yAxis={[{ scaleType: 'band', data: topAlertas.map(p => p.tipo) || ['Sem dados'] }]}
                                    series={[{ data: topAlertas.map(p => p.quantidade) || [0], color: '#6366f1', label: 'Repetições Encontradas' }]}
                                    height={350}
                                    margin={{ left: 180 }}
                                />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {tabValue === 1 && (
                <Grid container spacing={4}>
                    {/* Lado Esquerdo: Upload e Análise */}
                    <Grid item xs={12} md={viewMode === 'pivot' ? 12 : 7}>
                        <Paper className="premium-panel" sx={{ p: 4, borderRadius: 3 }}>
                            <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                                <CloudUploadIcon sx={{ mr: 1 }} /> Nova Auditoria
                            </Typography>
                            <Box
                                sx={{
                                    border: '2px dashed #ccc',
                                    borderRadius: 2,
                                    p: 4,
                                    textAlign: 'center',
                                    bgcolor: 'rgba(0,0,0,0.02)',
                                    mb: 3
                                }}
                            >
                                <input
                                    accept=".xml"
                                    style={{ display: 'none' }}
                                    id="raised-button-file"
                                    type="file"
                                    multiple
                                    onChange={handleFileChange}
                                />
                                <label htmlFor="raised-button-file">
                                    <Button variant="contained" component="span" startIcon={<HospitalIcon />}>
                                        Selecionar Guias XML
                                    </Button>
                                </label>
                                {files.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {files.length} arquivo(s) selecionado(s):
                                        </Typography>
                                        <Box sx={{ maxHeight: 100, overflow: 'auto', mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                            {files.map((f, i) => (
                                                <Typography key={i} variant="caption" display="block">
                                                    • {f.name}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2">Escolha o Convênio:</Typography>
                                    {(!selectedConvenio || selectedConvenio === 'null') && (
                                        <Tooltip title="Obrigatorio para alimentar histórico">
                                            <Typography variant="caption" color="error" sx={{ ml: 1, display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                                                <ErrorIcon sx={{ fontSize: 16, mr: 0.5 }} /> Seleção Necessária
                                            </Typography>
                                        </Tooltip>
                                    )}
                                </Box>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    value={selectedConvenio}
                                    onChange={(e) => setSelectedConvenio(e.target.value)}
                                    SelectProps={{ native: true }}
                                    error={!selectedConvenio || selectedConvenio === 'null'}
                                >
                                    <option value="null">-- Selecione uma Operadora --</option>
                                    {convenios.map((c) => (
                                        <option key={c.id} value={c.id}>{c.nome}</option>
                                    ))}
                                </TextField>
                            </Box>

                            <Box display="flex" gap={2}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    onClick={handleAnalisar}
                                    disabled={files.length === 0 || loading}
                                    sx={{ py: 1.5, textTransform: 'none', fontSize: '1.1rem' }}
                                >
                                    {loading ? 'Processando Batch...' : 'Executar Auditoria'}
                                </Button>
                                <Tooltip title={(!selectedConvenio || selectedConvenio === 'null') ? "Selecione um convênio para habilitar o aprendizado" : "Usa estas guias como histórico para o sistema aprender os padrões do hospital"}>
                                    <span>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            onClick={handleAprender}
                                            disabled={files.length === 0 || loading || !selectedConvenio || selectedConvenio === 'null'}
                                            sx={{ textTransform: 'none', height: '100%' }}
                                        >
                                            Alimentar Histórico
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>

                            {loading && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="body2" color="primary" gutterBottom fontWeight="bold">
                                        {batchStatus}
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={batchProgress}
                                        sx={{ height: 10, borderRadius: 5 }}
                                    />
                                </Box>
                            )}

                            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                            {analiseAtual && (
                                <Box sx={{ mt: 4 }}>
                                    <Divider sx={{ mb: 3 }} />
                                    <Typography variant="h6" gutterBottom>Resultado da Análise</Typography>

                                    <Card variant="outlined" sx={{ mb: 3, borderLeft: `6px solid ${getScoreColor(analiseAtual.score)}` }}>
                                        <CardContent>
                                            <Grid container alignItems="center">
                                                <Grid item xs={8}>
                                                    <Typography variant="subtitle2" color="textSecondary">SCORE DE RISCO (GLOSA/ERRO)</Typography>
                                                    <Typography variant="h3" fontWeight="bold" sx={{ color: getScoreColor(analiseAtual.score) }}>
                                                        {analiseAtual.score}%
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    {analiseAtual.score > 50 ? <ErrorIcon color="error" sx={{ fontSize: 60 }} /> : <CheckCircleIcon color="success" sx={{ fontSize: 60 }} />}
                                                </Grid>
                                            </Grid>
                                            <Box sx={{ mt: 2 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={analiseAtual.score}
                                                    sx={{ height: 10, borderRadius: 5, bgcolor: '#eee', '& .MuiLinearProgress-bar': { bgcolor: getScoreColor(analiseAtual.score) } }}
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" fontWeight="bold">Tipo:</Typography>
                                                <TextField
                                                    select
                                                    size="small"
                                                    value={filterType}
                                                    onChange={(e) => setFilterType(e.target.value)}
                                                    SelectProps={{ native: true }}
                                                    sx={{ minWidth: 150 }}
                                                >
                                                    <option value="Todos">Todos os Tipos</option>
                                                    <option value="Possível Omissão">Possível Omissão</option>
                                                    <option value="Preço Atípico">Preço Atípico</option>
                                                    <option value="Preço Acima da regra (Manual)">Preço Acima da regra (Manual)</option>
                                                    <option value="Preço Acima da tabela do convênio">Preço Acima da tabela do convênio</option>
                                                    <option value="Preço abaixo da tabela do convênio">Preço abaixo da tabela do convênio</option>
                                                    <option value="Código não encontrado na tabela do convênio">Código não encontrado na tabela do convênio</option>
                                                    <option value="Guia Volumosa">Guia Volumosa</option>
                                                </TextField>
                                            </Box>

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" fontWeight="bold">Procedimento:</Typography>
                                                <TextField
                                                    select
                                                    size="small"
                                                    value={filterProcedure}
                                                    onChange={(e) => setFilterProcedure(e.target.value)}
                                                    SelectProps={{ native: true }}
                                                    sx={{ minWidth: 200, maxWidth: 300 }}
                                                >
                                                    <option value="Todos">Todos os Procedimentos</option>
                                                    {uniqueProcedures.map((p) => (
                                                        <option key={p.codigo} value={p.codigo}>{p.codigo} - {p.desc}</option>
                                                    ))}
                                                </TextField>
                                            </Box>

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" fontWeight="bold">Guia Prestador:</Typography>
                                                <TextField
                                                    select
                                                    size="small"
                                                    value={filterGuia}
                                                    onChange={(e) => setFilterGuia(e.target.value)}
                                                    SelectProps={{ native: true }}
                                                    sx={{ minWidth: 150 }}
                                                >
                                                    <option value="Todos">Todas as Guias</option>
                                                    {uniqueGuias.map((g) => (
                                                        <option key={g} value={g}>{g}</option>
                                                    ))}
                                                </TextField>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                            {filterType === 'Possível Omissão' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" fontWeight="bold">Probabilidade Mínima:</Typography>
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        value={minProb}
                                                        onChange={(e) => setMinProb(Number(e.target.value))}
                                                        inputProps={{ min: 0, max: 100 }}
                                                        sx={{ width: 80 }}
                                                    />
                                                    <Typography variant="body2">%</Typography>
                                                </Box>
                                            )}

                                            <Box display="flex" alignItems="center" gap={2}>
                                                <Typography variant="body2" color="textSecondary">
                                                    Exibindo {filteredAlerts.length} de {analiseAtual.alertas.length}
                                                </Typography>

                                                {filterProcedure !== 'Todos' && (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        color="error"
                                                        startIcon={<ErrorIcon />}
                                                        onClick={() => handleIgnoreAllForProcedure(filterProcedure)}
                                                    >
                                                        Ignorar Omissões deste Procedimento
                                                    </Button>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Alertas Detectados (Clique para detalhes)
                                        </Typography>
                                        <Box display="flex" gap={1}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<VisibilityIcon />}
                                                onClick={() => handleOpenPivotNewTab(analiseAtual.alertas, `Análise de Alertas - Guia ${analiseAtual.guia}`)}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Abrir em Nova Aba
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={viewMode === 'list' ? <TableChartIcon /> : <ListIcon />}
                                                onClick={() => setViewMode(viewMode === 'list' ? 'pivot' : 'list')}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                {viewMode === 'list' ? 'Ver Tabela Dinâmica' : 'Ver em Lista'}
                                            </Button>
                                        </Box>
                                    </Box>

                                    {viewMode === 'pivot' ? (
                                        <PivotTabelaProcessada dados={analiseAtual.alertas} />
                                    ) : (
                                        <List>
                                            {filteredAlerts.length === 0 ? (
                                                <ListItem><ListItemText primary="Nenhum alerta corresponde aos filtros aplicados." /></ListItem>
                                            ) : (
                                                filteredAlerts.map((alerta, idx) => (
                                                    <Paper
                                                        key={idx}
                                                        elevation={0}
                                                        className="alert-item-hover"
                                                        onClick={() => handleShowDetails(alerta)}
                                                        sx={{
                                                            mb: 1,
                                                            cursor: 'pointer',
                                                            bgcolor: alerta.nivel === 'Alto' ? '#fff5f5' : '#fff9f0',
                                                            border: '1px solid',
                                                            borderColor: alerta.nivel === 'Alto' ? '#ffcfcf' : '#ffe8cc',
                                                            '&:hover': {
                                                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                                                borderColor: 'primary.main'
                                                            }
                                                        }}
                                                    >
                                                        <ListItem>
                                                            <ListItemIcon>
                                                                {alerta.nivel === 'Alto' ? <ErrorIcon color="error" /> : <WarningIcon color="warning" />}
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={<strong>{alerta.tipo}</strong>}
                                                                secondary={
                                                                    <Box component="span">
                                                                        <Typography variant="body2" color="textPrimary" display="block" sx={{ mb: 0.5 }}>
                                                                            {alerta.descricao}
                                                                        </Typography>
                                                                        {(alerta.paciente || alerta.guia) && (
                                                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                                                                                {alerta.paciente && `Paciente: ${alerta.paciente}`}
                                                                                {alerta.paciente && alerta.guia && ' | '}
                                                                                {alerta.guia && `Guia Prestador: ${alerta.guia}`}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                }
                                                            />
                                                            <Box display="flex" gap={1} alignItems="center" onClick={(e) => e.stopPropagation()}>
                                                                {alerta.codigo && (
                                                                    <Chip
                                                                        label={`Item: ${alerta.codigo}`}
                                                                        variant="outlined"
                                                                        size="small"
                                                                        color="primary"
                                                                        sx={{ fontWeight: 'bold' }}
                                                                    />
                                                                )}
                                                                <Chip label={alerta.nivel} color={alerta.nivel === 'Alto' ? 'error' : 'warning'} size="small" />
                                                                {alerta.feedback ? (
                                                                    <Chip
                                                                        label={alerta.feedback === 'rejeitar' ? 'Ignorado' : 'Aceito'}
                                                                        variant="outlined"
                                                                        color={alerta.feedback === 'rejeitar' ? 'default' : 'success'}
                                                                        size="small"
                                                                        icon={alerta.feedback === 'rejeitar' ? <ErrorIcon /> : <CheckCircleIcon />}
                                                                    />
                                                                ) : (
                                                                    <Box>
                                                                        <Tooltip title="Aceitar Sugestão">
                                                                            <IconButton size="small" color="success" onClick={() => handleFeedback(idx, 'aceitar')}>
                                                                                <CheckCircleIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title="Rejeitar/Ignorar este padrão">
                                                                            <IconButton size="small" color="error" onClick={() => handleFeedback(idx, 'rejeitar')}>
                                                                                <ErrorIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </ListItem>
                                                    </Paper>
                                                ))
                                            )}
                                        </List>
                                    )}
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Lado Direito: Histórico de Auditorias */}
                    {viewMode !== 'pivot' && (
                        <Grid item xs={12} md={5}>
                            <Paper className="premium-panel" sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                                <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                                    <HistoryIcon sx={{ mr: 1 }} /> Histórico Recente
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                <List sx={{ maxHeight: 600, overflow: 'auto' }}>
                                    {historico.map((item) => (
                                        <React.Fragment key={item.id}>
                                            <ListItem
                                                secondaryAction={
                                                    <Box>
                                                        <Tooltip title="Carregar Resultados">
                                                            <IconButton edge="end" aria-label="load" onClick={() => setAnaliseAtual(item)} color="info" size="small">
                                                                <TrendingUpIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Ver Itens">
                                                            <IconButton edge="end" aria-label="view" onClick={() => handleShowDetails(item, true)} color="primary" size="small" sx={{ ml: 1 }}>
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Exportar PDF">
                                                            <IconButton edge="end" aria-label="pdf" onClick={() => handleExportarRelatorio(item.id)} color="secondary" size="small" sx={{ ml: 1 }}>
                                                                <PictureAsPdfIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                }
                                                sx={{ px: 0 }}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ pr: 8 }}>
                                                            <Typography variant="body1" fontWeight="bold">Guia: {item.guia}</Typography>
                                                            <Chip
                                                                label={`${item.score}%`}
                                                                size="small"
                                                                sx={{ bgcolor: getScoreColor(item.score), color: 'white', fontWeight: 'bold' }}
                                                            />
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Box component="span" display="block" sx={{ pr: 8 }}>
                                                            <Typography variant="caption" color="textSecondary">{item.data}</Typography>
                                                            <Box mt={0.5} display="flex" gap={0.5} flexWrap="wrap">
                                                                {item.alertas.slice(0, 2).map((a, i) => (
                                                                    <Chip key={i} label={a.tipo} size="tiny" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                                                ))}
                                                                {item.alertas.length > 2 && <Typography variant="caption">+{item.alertas.length - 2}</Typography>}
                                                            </Box>
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                            <Divider component="li" />
                                        </React.Fragment>
                                    ))}
                                    {historico.length === 0 && (
                                        <Box textAlign="center" py={4} color="textSecondary">
                                            Nenhuma auditoria realizada ainda.
                                        </Box>
                                    )}
                                </List>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            )}


            {/* Modal de Detalhes Históricos */}
            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h6">Detalhamento de Alerta: {selectedAlerta?.tipo}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>Item: {selectedAlerta?.codigo || selectedAlerta?.procedimento_codigo}</Typography>
                    </Box>
                    <IconButton onClick={() => setModalOpen(false)} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {loadingDetalhes ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={8}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer component={Paper} elevation={0}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Data Exec.</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Guia Prest.</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Guia Oper.</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Carteira</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Paciente</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Início/Fim</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Código</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Descrição</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Qtde</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {detalhesAlerta.map((row, i) => (
                                        <TableRow key={i} hover>
                                            <TableCell>
                                                <Chip label={row.status || 'Faturado'} size="small" variant="outlined" color="info" sx={{ fontSize: '0.7rem' }} />
                                            </TableCell>
                                            <TableCell>{row.data_execucao}</TableCell>
                                            <TableCell sx={{ fontWeight: 500 }}>{row.guia}</TableCell>
                                            <TableCell>{row.guia_operadora}</TableCell>
                                            <TableCell>{row.carteira}</TableCell>
                                            <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {row.paciente}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" display="block">{row.data_inicio}</Typography>
                                                <Typography variant="caption" display="block">{row.data_fim}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>{row.codigo}</TableCell>
                                            <TableCell sx={{ maxWidth: 250 }}>{row.descricao}</TableCell>
                                            <TableCell align="center">{row.quantidade}</TableCell>
                                        </TableRow>
                                    ))}
                                    {detalhesAlerta.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center" sx={{ py: 8, color: 'text.secondary' }}>
                                                Nenhum registro histórico encontrado para este item.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                    <Button
                        onClick={() => handleOpenPivotNewTab(detalhesAlerta, `Tabela Dinâmica: ${selectedAlerta?.tipo}`)}
                        variant="outlined"
                        color="secondary"
                        startIcon={<TableChartIcon />}
                        disabled={detalhesAlerta.length === 0}
                    >
                        Ver em Tabela Dinâmica
                    </Button>
                    <Button onClick={() => setModalOpen(false)} variant="contained" color="primary">Fechar</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AuditoriaTissDashboard;
